import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DEFAULT_HOURS = {
  enabled: true,
  start: '09:00',
  end: '18:00',
};

const DEFAULT_SCHEDULE = {
  monday: { ...DEFAULT_HOURS },
  tuesday: { ...DEFAULT_HOURS },
  wednesday: { ...DEFAULT_HOURS },
  thursday: { ...DEFAULT_HOURS },
  friday: { ...DEFAULT_HOURS },
  saturday: { enabled: false, start: '09:00', end: '18:00' },
  sunday: { enabled: false, start: '09:00', end: '18:00' },
};

export default function BusinessHoursEditor({ value, onChange, timezone }) {
  const { t } = useTranslation('bots');
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [is24_7, setIs24_7] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const DAYS = DAY_KEYS.map(key => ({ key, label: t(`hours.days.${key}`) }));

  // Initialize schedule from value prop
  useEffect(() => {
    if (value && typeof value === 'object') {
      // Normalize incoming value: convert null days (execution format) to UI format
      const normalizedSchedule = {};
      DAY_KEYS.forEach((key) => {
        const day = value[key];
        if (!day || typeof day !== 'object') {
          // null/undefined = closed day
          normalizedSchedule[key] = { enabled: false, start: '09:00', end: '18:00' };
        } else {
          normalizedSchedule[key] = {
            enabled: day.enabled !== undefined ? Boolean(day.enabled) : Boolean(day.start && day.end),
            start: day.start || '09:00',
            end: day.end || '18:00',
          };
        }
      });

      // Check if it's a 24/7 schedule
      const all24_7 = value.is24x7 || DAY_KEYS.every(
        key => normalizedSchedule[key]?.enabled &&
               normalizedSchedule[key]?.start === '00:00' &&
               normalizedSchedule[key]?.end === '23:59'
      );

      setIs24_7(all24_7);
      setSchedule(normalizedSchedule);
    }
  }, [value]);

  const validateTime = (day, start, end) => {
    if (start >= end) {
      return t('hours.validationCloseAfterOpen');
    }
    return null;
  };

  const updateSchedule = (newSchedule, new24_7 = is24_7) => {
    setSchedule(newSchedule);

    // Validate all enabled days
    const errors = {};
    DAY_KEYS.forEach((key) => {
      if (newSchedule[key] && newSchedule[key].enabled && !new24_7) {
        const error = validateTime(
          key,
          newSchedule[key].start,
          newSchedule[key].end
        );
        if (error) {
          errors[key] = error;
        }
      }
    });

    setValidationErrors(errors);

    // Only call onChange if there are no validation errors
    if (Object.keys(errors).length === 0) {
      onChange(newSchedule);
    }
  };

  const handleDayToggle = (dayKey) => {
    const newSchedule = {
      ...schedule,
      [dayKey]: {
        ...schedule[dayKey],
        enabled: !schedule[dayKey].enabled,
      },
    };
    updateSchedule(newSchedule);
  };

  const handleTimeChange = (dayKey, field, value) => {
    const newSchedule = {
      ...schedule,
      [dayKey]: {
        ...schedule[dayKey],
        [field]: value,
      },
    };
    updateSchedule(newSchedule);
  };

  const handleCopyToAll = () => {
    if (!window.confirm(t('hours.confirmCopyToAll'))) {
      return;
    }

    const mondaySchedule = schedule.monday;
    const newSchedule = {};

    DAY_KEYS.forEach((key) => {
      newSchedule[key] = { ...mondaySchedule };
    });

    updateSchedule(newSchedule);
  };

  const handle24_7Toggle = () => {
    const new24_7 = !is24_7;
    setIs24_7(new24_7);

    if (new24_7) {
      // Set all days to 24/7
      const newSchedule = {};
      DAY_KEYS.forEach((key) => {
        newSchedule[key] = {
          enabled: true,
          start: '00:00',
          end: '23:59',
        };
      });
      updateSchedule(newSchedule, true);
    } else {
      // Reset to default schedule
      updateSchedule(DEFAULT_SCHEDULE, false);
    }
  };

  const generatePreviewText = () => {
    if (is24_7) {
      return t('hours.available24_7');
    }

    const enabledDays = DAYS.filter(day => schedule[day.key]?.enabled);

    if (enabledDays.length === 0) {
      return t('hours.noSchedule');
    }

    // Group consecutive days with same hours
    const groups = [];
    let currentGroup = null;

    enabledDays.forEach((day, index) => {
      const hours = schedule[day.key] || {};
      const timeRange = `${formatTime(hours.start || '09:00')} - ${formatTime(hours.end || '18:00')}`;

      if (!currentGroup || currentGroup.timeRange !== timeRange) {
        // Start new group
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          startDay: day.label,
          endDay: day.label,
          timeRange,
          dayKeys: [day.key],
        };
      } else {
        // Extend current group
        currentGroup.endDay = day.label;
        currentGroup.dayKeys.push(day.key);
      }

      // Push last group
      if (index === enabledDays.length - 1) {
        groups.push(currentGroup);
      }
    });

    // Format groups
    return groups
      .map(group => {
        const dayRange =
          group.startDay === group.endDay
            ? group.startDay
            : `${group.startDay} a ${group.endDay}`;
        return `${dayRange}: ${group.timeRange}`;
      })
      .join('\n');
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="business-hours-editor">
      <div className="editor-header">
        <div className="header-title">
          <h4>{t('hours.title')}</h4>
          {timezone && (
            <span className="timezone-badge">
              {t('hours.timezone')} {timezone}
            </span>
          )}
        </div>

        <div className="quick-actions">
          <button
            onClick={handle24_7Toggle}
            className={`btn-toggle-24-7 ${is24_7 ? 'active' : ''}`}
          >
            {is24_7 ? t('hours.24_7Active') : t('hours.24_7')}
          </button>
          {!is24_7 && (
            <button onClick={handleCopyToAll} className="btn-copy-all">
              {t('hours.copyToAll')}
            </button>
          )}
        </div>
      </div>

      {!is24_7 && (
        <div className="days-list">
          {DAYS.map(({ key, label }) => (
            <div key={key} className="day-row">
              <div className="day-toggle">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={schedule[key].enabled}
                    onChange={() => handleDayToggle(key)}
                  />
                  <span className="day-name">{label}</span>
                </label>
              </div>

              {schedule[key].enabled && (
                <div className="time-pickers">
                  <div className="time-group">
                    <label>{t('hours.open')}</label>
                    <input
                      type="time"
                      value={schedule[key].start}
                      onChange={(e) =>
                        handleTimeChange(key, 'start', e.target.value)
                      }
                      className="time-input"
                    />
                  </div>
                  <span className="time-separator">-</span>
                  <div className="time-group">
                    <label>{t('hours.close')}</label>
                    <input
                      type="time"
                      value={schedule[key].end}
                      onChange={(e) =>
                        handleTimeChange(key, 'end', e.target.value)
                      }
                      className="time-input"
                    />
                  </div>
                </div>
              )}

              {schedule[key].enabled && !schedule[key].start && !schedule[key].end && (
                <span className="closed-label">{t('hours.closed')}</span>
              )}

              {validationErrors[key] && (
                <div className="validation-error">
                  {validationErrors[key]}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="preview-section">
        <h5>{t('hours.previewTitle')}</h5>
        <div className="preview-box">
          {generatePreviewText().split('\n').map((line, index) => (
            <div key={index} className="preview-line">
              {line}
            </div>
          ))}
        </div>
      </div>

      {Object.keys(validationErrors).length > 0 && (
        <div className="global-error">
          {t('hours.fixValidationErrors')}
        </div>
      )}

      <style>{`
        .business-hours-editor {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
        }

        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .header-title h4 {
          margin: 0 0 0.5rem 0;
          color: #2d3748;
          font-size: 1.125rem;
          font-weight: 600;
        }

        .timezone-badge {
          display: inline-block;
          font-size: 0.75rem;
          color: #718096;
          background: #f7fafc;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          border: 1px solid #e2e8f0;
        }

        .quick-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn-toggle-24-7,
        .btn-copy-all {
          padding: 0.5rem 1rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          background: white;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-toggle-24-7:hover,
        .btn-copy-all:hover {
          background: #f7fafc;
          border-color: #667eea;
        }

        .btn-toggle-24-7.active {
          background: #667eea;
          color: white;
          border-color: #667eea;
        }

        .btn-toggle-24-7.active:hover {
          background: #5568d3;
        }

        .days-list {
          margin-bottom: 1.5rem;
        }

        .day-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem;
          border-bottom: 1px solid #f7fafc;
          flex-wrap: wrap;
        }

        .day-row:hover {
          background: #f7fafc;
        }

        .day-toggle {
          min-width: 120px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          user-select: none;
        }

        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .day-name {
          font-weight: 600;
          color: #2d3748;
          font-size: 0.9375rem;
        }

        .time-pickers {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
        }

        .time-group {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .time-group label {
          font-size: 0.75rem;
          color: #718096;
          font-weight: 600;
        }

        .time-input {
          padding: 0.5rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 0.875rem;
          width: 120px;
        }

        .time-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .time-separator {
          color: #a0aec0;
          font-weight: 600;
          margin-top: 1.25rem;
        }

        .closed-label {
          color: #a0aec0;
          font-style: italic;
          font-size: 0.875rem;
        }

        .validation-error {
          width: 100%;
          color: #e53e3e;
          font-size: 0.8125rem;
          margin-top: 0.25rem;
          padding-left: 136px;
        }

        .preview-section {
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 1rem;
          margin-top: 1.5rem;
        }

        .preview-section h5 {
          margin: 0 0 0.75rem 0;
          color: #4a5568;
          font-size: 0.875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .preview-box {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          padding: 0.75rem;
          min-height: 60px;
        }

        .preview-line {
          color: #2d3748;
          font-size: 0.9375rem;
          line-height: 1.6;
          padding: 0.25rem 0;
        }

        .preview-line:not(:last-child) {
          border-bottom: 1px solid #f7fafc;
          margin-bottom: 0.25rem;
          padding-bottom: 0.5rem;
        }

        .global-error {
          background: #fff5f5;
          border: 1px solid #fc8181;
          color: #742a2a;
          padding: 0.75rem 1rem;
          border-radius: 6px;
          margin-top: 1rem;
          font-size: 0.875rem;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .editor-header {
            flex-direction: column;
            gap: 1rem;
          }

          .quick-actions {
            width: 100%;
            flex-direction: column;
          }

          .btn-toggle-24-7,
          .btn-copy-all {
            width: 100%;
          }

          .day-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .day-toggle {
            min-width: auto;
          }

          .time-pickers {
            width: 100%;
            flex-direction: column;
            gap: 0.5rem;
          }

          .time-separator {
            display: none;
          }

          .time-group {
            width: 100%;
          }

          .time-input {
            width: 100%;
            box-sizing: border-box;
          }

          .validation-error {
            padding-left: 0;
          }
        }
      `}</style>
    </div>
  );
}
